import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@store/authStore';
import useDailyPlanStore, { DayState, PlanTask } from '@/store/Employee/DailyPlanStore';
import PlanForTheDayPanel from './PlanForTheDayPanel';
import EodReportPanel from './EodReportPanel';

/**
 * A FROZEN copy of what the gate is currently asking for.
 *
 * Deliberately not a pointer into `dayState`: submitting rewrites `dayState`
 * immediately, so anything read from the store during render would be swapped out
 * from under the panel the instant its request resolves — the panel would unmount
 * mid-submit, its completion moment would never play, and a queued day would look
 * like the same drawer reopening itself.
 */
type ActiveGate =
  | { kind: 'retro_eod'; date: string; tasks: PlanTask[]; index: number; total: number }
  | { kind: 'plan' }
  | null;

/**
 * App-wide mandatory daily gate. Mounted once at the app root (inside Router,
 * outside Routes) so it appears on the first authenticated screen regardless of
 * landing route and survives navigation (state lives in the store).
 *
 * Applies to every authenticated user — for attendance, everyone is an employee.
 *
 * Precedence (server-decided via gateRequired):
 *   retro_eod  -> reconcile a forgotten prior-day EOD first (blocking)
 *   plan       -> submit today's Plan for the Day (blocking)
 *   null       -> nothing to show
 *
 * The active gate is held in LOCAL state as a snapshot (see ActiveGate). The panel
 * signals `onDone()` when it has finished celebrating, and only then do we advance
 * — which makes retro -> retro -> plan one continuous handoff instead of a series
 * of overlays snapping shut and firing again.
 */
const DailyGate: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const dayState = useDailyPlanStore((s) => s.dayState);
  const fetchDayState = useDailyPlanStore((s) => s.fetchDayState);

  const [active, setActive] = useState<ActiveGate>(null);

  // How many days the backlog held when we first opened it, so the drawer can say
  // "Day 2 of 3" rather than appearing to reopen itself after every submit.
  const backlogTotal = useRef(0);

  const gated = !!(isAuthenticated && user);

  /** Freeze whatever the server is currently asking for into a self-contained gate. */
  const snapshot = useCallback((state: DayState | null): ActiveGate => {
    if (!state?.gateRequired) return null;

    if (state.gateRequired === 'retro_eod' && state.pendingRetroEod) {
      // Older servers don't send the count; one pending day is still one day.
      const remaining = state.pendingRetroEodCount || 1;
      if (backlogTotal.current === 0) backlogTotal.current = remaining;
      return {
        kind: 'retro_eod',
        date: state.pendingRetroEod.date,
        tasks: state.pendingRetroEod.tasks,
        index: Math.max(1, backlogTotal.current - remaining + 1),
        total: backlogTotal.current
      };
    }

    if (state.gateRequired === 'plan') return { kind: 'plan' };
    return null;
  }, []);

  // Fetch once when the gate becomes applicable / user changes.
  useEffect(() => {
    if (gated) fetchDayState();
  }, [gated, user?.id, fetchDayState]);

  // Adopt a gate only while idle. Never clear here — the panel clears it via
  // onDone, so a submit can't yank the panel out from under its own animation.
  useEffect(() => {
    if (!gated) {
      backlogTotal.current = 0;
      setActive(null);
      return;
    }
    if (!active && dayState?.gateRequired) setActive(snapshot(dayState));
  }, [gated, active, dayState, snapshot]);

  // If logged in before the window opens, re-fetch when it opens so the plan
  // panel appears without a manual refresh. Server stays authoritative.
  useEffect(() => {
    if (!gated || !dayState) return undefined;
    if (dayState.gateRequired) return undefined; // already showing something
    if (!dayState.isWorkingToday || dayState.planSubmitted) return undefined;
    if (dayState.window.withinWindow) return undefined;

    const minutesUntilOpen = dayState.window.opensAtMinutes - dayState.window.nowMinutes;
    if (minutesUntilOpen <= 0) return undefined;

    const ms = Math.min(minutesUntilOpen * 60 * 1000 + 1000, 2 ** 31 - 1);
    const timer = window.setTimeout(() => fetchDayState(), ms);
    return () => window.clearTimeout(timer);
  }, [gated, dayState, fetchDayState]);

  /**
   * The panel has finished its completion moment. Jump straight to whatever the
   * server now requires — the next unreconciled day, or the plan — with no gap.
   */
  const handleDone = useCallback(() => {
    const next = snapshot(useDailyPlanStore.getState().dayState);
    if (!next) backlogTotal.current = 0;
    setActive(next);
  }, [snapshot]);

  /** Blocking panels can't be dismissed, so a dead server has to be recoverable. */
  const handleRetry = useCallback(() => {
    backlogTotal.current = 0;
    setActive(null); // re-adopted from the refreshed state by the effect above
    fetchDayState();
  }, [fetchDayState]);

  if (!gated || !active) return null;

  if (active.kind === 'retro_eod') {
    return (
      <EodReportPanel
        key={`retro-${active.date}`}
        open
        tone="retro"
        forDate={active.date}
        tasks={active.tasks}
        dayIndex={active.index}
        dayTotal={active.total}
        onDone={handleDone}
        onRetry={handleRetry}
      />
    );
  }

  return <PlanForTheDayPanel key="plan" open onDone={handleDone} onRetry={handleRetry} />;
};

export default DailyGate;
